/**
 * Pure domain logic for Dual Power Feed (A/B) Redundancy and Failover Modeling.
 * Standard Electrical Engineering Compliance: NEC / NFPA 70 80% Continuous Load Derating.
 */

export const SAFE_CONTINUOUS_LOAD_FACTOR = 0.80;
// ponytail: 230V standard European single-phase nominal voltage ceiling; add per-rack/feed voltage config when 110V/208V/3-phase needed
export const DEFAULT_VOLTAGE_V = 230;
export const DEFAULT_AMPERAGE_A = 16;

export type PsuFeedMode = 'A+B' | 'A' | 'B';

export interface FeedCapacity {
  ratedAmperageA: number;
  voltageV: number;
  ratedPowerW: number;
  safeLimitW: number; // 80% continuous rating
}

export interface FeedState {
  id: 'A' | 'B';
  name: string;
  capacity: FeedCapacity;
  normalDrawW: number;
  normalLoadPct: number;
  normalCurrentA: number;
  normalExceedsSafe: boolean;
  normalExceedsTrip: boolean;
  failoverDrawW: number;
  failoverLoadPct: number;
  failoverCurrentA: number;
  failoverExceedsSafe: boolean;
  failoverExceedsTrip: boolean;
}

export interface RackPowerRedundancyOptions {
  amperageA?: number;
  amperageB?: number;
  voltageV?: number;
  rackMaxPowerW?: number;
}

export interface ConsumerItem {
  id: string;
  name: string;
  powerW: number;
  psuFeed: PsuFeedMode;
  drawA: number;
  drawB: number;
}

export interface RackPowerRedundancyResult {
  totalLoadW: number;
  feedA: FeedState;
  feedB: FeedState;
  isRedundant: boolean;
  hasSpofRisk: boolean;
  status: 'redundant' | 'spof_risk' | 'critical_overload';
  alerts: string[];
  consumers: ConsumerItem[];
  consumerCounts: {
    dualPsu: number;
    singleA: number;
    singleB: number;
    unpowered: number;
  };
}

/**
 * Calculates rated power and 80% continuous safety limit for a feed.
 */
export function calculateFeedCapacity(
  amperageA: number = DEFAULT_AMPERAGE_A,
  voltageV: number = DEFAULT_VOLTAGE_V
): FeedCapacity {
  const amp = Math.max(1, Number(amperageA) || DEFAULT_AMPERAGE_A);
  const volt = Math.max(100, Number(voltageV) || DEFAULT_VOLTAGE_V);
  const ratedPowerW = Math.round(amp * volt);
  const safeLimitW = Math.round(ratedPowerW * SAFE_CONTINUOUS_LOAD_FACTOR);

  return {
    ratedAmperageA: amp,
    voltageV: volt,
    ratedPowerW,
    safeLimitW,
  };
}

/**
 * Resolves feed capacity from either explicit amperage/voltage or rack max_power_w.
 */
export function resolveFeedCapacity(
  options: RackPowerRedundancyOptions = {},
  feedId: 'A' | 'B'
): FeedCapacity {
  const explicitAmp = feedId === 'A' ? options.amperageA : options.amperageB;
  const volt = options.voltageV || DEFAULT_VOLTAGE_V;

  if (explicitAmp != null && explicitAmp > 0) {
    return calculateFeedCapacity(explicitAmp, volt);
  }

  // If rackMaxPowerW is specified and reasonable (>0)
  if (options.rackMaxPowerW != null && options.rackMaxPowerW > 0) {
    const ratedPowerW = Math.round(options.rackMaxPowerW);
    const safeLimitW = Math.round(ratedPowerW * SAFE_CONTINUOUS_LOAD_FACTOR);
    const ratedAmperageA = Math.round((ratedPowerW / volt) * 10) / 10;
    return {
      ratedAmperageA,
      voltageV: volt,
      ratedPowerW,
      safeLimitW,
    };
  }

  // Fallback to standard 16A @ 230V
  return calculateFeedCapacity(DEFAULT_AMPERAGE_A, volt);
}

/**
 * Normalizes device power feed configuration from device attributes.
 * Defaults to 'A+B' (dual redundant PSU) for enterprise rack equipment.
 */
export function resolvePsuFeed(eq: any): PsuFeedMode {
  let attrs = eq?.attributes || {};
  if (typeof attrs === 'string') {
    try {
      attrs = JSON.parse(attrs);
    } catch {
      attrs = {};
    }
  }
  const rawFeed = (attrs.psu_feed || attrs.power_feed || attrs.feed || '').toString().trim().toUpperCase();

  if (rawFeed === 'A') return 'A';
  if (rawFeed === 'B') return 'B';
  if (rawFeed === 'A+B' || rawFeed === 'BOTH' || rawFeed === 'DUAL' || rawFeed === 'REDUNDANT') {
    return 'A+B';
  }

  // Check if name contains feed hints
  const nameUpper = (eq?.name || '').toUpperCase();
  if (nameUpper.includes('FEED-A') || nameUpper.includes('PDU-A')) return 'A';
  if (nameUpper.includes('FEED-B') || nameUpper.includes('PDU-B')) return 'B';

  return 'A+B';
}

/**
 * Computes dual-feed power distribution, failover surge, and SPOF redundancy risks.
 */
export function calculateRackPowerRedundancy(
  equipmentList: any[] = [],
  options: RackPowerRedundancyOptions = {}
): RackPowerRedundancyResult {
  const capA = resolveFeedCapacity(options, 'A');
  const capB = resolveFeedCapacity(options, 'B');

  let feedADrawW = 0;
  let feedBDrawW = 0;
  let dualDrawW = 0;
  let singleADrawW = 0;
  let singleBDrawW = 0;

  let dualPsuCount = 0;
  let singleACount = 0;
  let singleBCount = 0;
  let unpoweredCount = 0;

  const consumers: ConsumerItem[] = [];

  for (const eq of equipmentList) {
    if (!eq) continue;
    if (eq.is_zero_u || eq.equipment_type?.toLowerCase() === 'pdu') continue;
    const rawPower = eq.power_w ?? eq.power_watts ?? eq.rated_power_w ?? 0;
    const powerW = Math.max(0, Number(rawPower) || 0);

    if (powerW === 0) {
      unpoweredCount++;
      continue;
    }

    const psuFeed = resolvePsuFeed(eq);
    let drawA = 0;
    let drawB = 0;

    if (psuFeed === 'A+B') {
      dualPsuCount++;
      drawA = Math.round(powerW / 2);
      drawB = powerW - drawA;
      dualDrawW += powerW;
    } else if (psuFeed === 'A') {
      singleACount++;
      drawA = powerW;
      drawB = 0;
      singleADrawW += powerW;
    } else if (psuFeed === 'B') {
      singleBCount++;
      drawA = 0;
      drawB = powerW;
      singleBDrawW += powerW;
    }

    feedADrawW += drawA;
    feedBDrawW += drawB;

    consumers.push({
      id: String(eq.id),
      name: eq.name || 'Оборудование',
      powerW,
      psuFeed,
      drawA,
      drawB,
    });
  }

  const totalLoadW = feedADrawW + feedBDrawW;

  // Failover modeling:
  // If Feed A fails: Feed B must support all dual-PSU equipment (100% of their load) + its own single-PSU equipment.
  const failoverDrawOnB = dualDrawW + singleBDrawW;

  // If Feed B fails: Feed A must support all dual-PSU equipment + its own single-PSU equipment.
  const failoverDrawOnA = dualDrawW + singleADrawW;

  // Calculate percentages
  const normalLoadPctA = Math.round((feedADrawW / capA.ratedPowerW) * 100);
  const normalLoadPctB = Math.round((feedBDrawW / capB.ratedPowerW) * 100);

  const failoverLoadPctA = Math.round((failoverDrawOnA / capA.ratedPowerW) * 100);
  const failoverLoadPctB = Math.round((failoverDrawOnB / capB.ratedPowerW) * 100);

  const normalCurrentA = Math.round((feedADrawW / capA.voltageV) * 10) / 10;
  const normalCurrentB = Math.round((feedBDrawW / capB.voltageV) * 10) / 10;

  const failoverCurrentA = Math.round((failoverDrawOnA / capA.voltageV) * 10) / 10;
  const failoverCurrentB = Math.round((failoverDrawOnB / capB.voltageV) * 10) / 10;

  const normalExceedsSafeA = feedADrawW > capA.safeLimitW;
  const normalExceedsSafeB = feedBDrawW > capB.safeLimitW;
  const normalExceedsTripA = feedADrawW > capA.ratedPowerW;
  const normalExceedsTripB = feedBDrawW > capB.ratedPowerW;

  const failoverExceedsSafeA = failoverDrawOnA > capA.safeLimitW;
  const failoverExceedsSafeB = failoverDrawOnB > capB.safeLimitW;
  const failoverExceedsTripA = failoverDrawOnA > capA.ratedPowerW;
  const failoverExceedsTripB = failoverDrawOnB > capB.ratedPowerW;

  const hasTripRisk = failoverExceedsTripA || failoverExceedsTripB || normalExceedsTripA || normalExceedsTripB;
  const hasSpofRisk = failoverExceedsSafeA || failoverExceedsSafeB || normalExceedsSafeA || normalExceedsSafeB;
  const isRedundant = !hasSpofRisk;

  let status: 'redundant' | 'spof_risk' | 'critical_overload' = 'redundant';
  if (hasTripRisk) {
    status = 'critical_overload';
  } else if (hasSpofRisk) {
    status = 'spof_risk';
  }

  // Compile specific engineering alerts
  const alerts: string[] = [];

  if (status === 'critical_overload') {
    if (normalExceedsTripA) {
      alerts.push(
        `Перегрузка: Ввод A перегружен в нормальном режиме (${feedADrawW} Вт > ${capA.ratedPowerW} Вт)! Отключение автомата.`
      );
    }
    if (normalExceedsTripB) {
      alerts.push(
        `Перегрузка: Ввод B перегружен в нормальном режиме (${feedBDrawW} Вт > ${capB.ratedPowerW} Вт)! Отключение автомата.`
      );
    }
    if (failoverExceedsTripB) {
      alerts.push(
        `Перегрузка: Отказ ввода A вызовет отключение автомата ввода B (${failoverDrawOnB} Вт > ${capB.ratedPowerW} Вт)!`
      );
    }
    if (failoverExceedsTripA) {
      alerts.push(
        `Перегрузка: Отказ ввода B вызовет отключение автомата ввода A (${failoverDrawOnA} Вт > ${capA.ratedPowerW} Вт)!`
      );
    }
  } else if (status === 'spof_risk') {
    if (normalExceedsSafeA) {
      alerts.push(
        `Ввод A превышает безопасный предел длительной нагрузки 80% в нормальном режиме (${feedADrawW} Вт > ${capA.safeLimitW} Вт (NEC 80%)).`
      );
    }
    if (normalExceedsSafeB) {
      alerts.push(
        `Ввод B превышает безопасный предел длительной нагрузки 80% в нормальном режиме (${feedBDrawW} Вт > ${capB.safeLimitW} Вт (NEC 80%)).`
      );
    }
    if (failoverExceedsSafeB) {
      alerts.push(
        `SPOF: Риск каскадного отключения при отказе ввода A (нагрузка на ввод B составит ${failoverDrawOnB} Вт / ${failoverLoadPctB}%, превышая безопасный предел 80% в ${capB.safeLimitW} Вт).`
      );
    }
    if (failoverExceedsSafeA) {
      alerts.push(
        `SPOF: Риск каскадного отключения при отказе ввода B (нагрузка на ввод A составит ${failoverDrawOnA} Вт / ${failoverLoadPctA}%, превышая безопасный предел 80% в ${capA.safeLimitW} Вт).`
      );
    }
  }

  return {
    totalLoadW,
    feedA: {
      id: 'A',
      name: 'Ввод A',
      capacity: capA,
      normalDrawW: feedADrawW,
      normalLoadPct: normalLoadPctA,
      normalCurrentA,
      normalExceedsSafe: normalExceedsSafeA,
      normalExceedsTrip: normalExceedsTripA,
      failoverDrawW: failoverDrawOnA,
      failoverLoadPct: failoverLoadPctA,
      failoverCurrentA,
      failoverExceedsSafe: failoverExceedsSafeA,
      failoverExceedsTrip: failoverExceedsTripA,
    },
    feedB: {
      id: 'B',
      name: 'Ввод B',
      capacity: capB,
      normalDrawW: feedBDrawW,
      normalLoadPct: normalLoadPctB,
      normalCurrentA: normalCurrentB,
      normalExceedsSafe: normalExceedsSafeB,
      normalExceedsTrip: normalExceedsTripB,
      failoverDrawW: failoverDrawOnB,
      failoverLoadPct: failoverLoadPctB,
      failoverCurrentA: failoverCurrentB,
      failoverExceedsSafe: failoverExceedsSafeB,
      failoverExceedsTrip: failoverExceedsTripB,
    },
    isRedundant,
    hasSpofRisk,
    status,
    alerts,
    consumers,
    consumerCounts: {
      dualPsu: dualPsuCount,
      singleA: singleACount,
      singleB: singleBCount,
      unpowered: unpoweredCount,
    },
  };
}
